function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
import { ValidationError } from '@deephaven/utils';
import FileNotFoundError from "./FileNotFoundError.js";
/**
 * A basic list of some common MIME types.
 */
export var MIME_TYPE = /*#__PURE__*/function (MIME_TYPE) {
  MIME_TYPE["GROOVY"] = "text/x-groovy";
  MIME_TYPE["PLAIN_TEXT"] = "text/plain";
  MIME_TYPE["PYTHON"] = "text/x-python";
  MIME_TYPE["PYTHON_COMPILED"] = "application/x-python-code";
  MIME_TYPE["SCALA"] = "text/x-scala";
  MIME_TYPE["UNKNOWN"] = "";
  return MIME_TYPE;
}({});

/**
 * Collection of utils for operating on file names
 */
export class FileUtils {
  static fileExists(fileStorage, name) {
    return _asyncToGenerator(function* () {
      try {
        yield fileStorage.info(name);
        return true;
      } catch (e) {
        // Should probably make sure it's a `FileNotFoundError`, and re-throw the error if it is not
        if (!(e instanceof FileNotFoundError)) {
          throw e;
        }
        return false;
      }
    })();
  }

  /**
   * Format file extension
   * @param extension File extension to format, defaults to empty string
   * @returns Formatted string - '' for no extension, '.' for empty extension, '.ext' for non-empty extension
   */
  static fileExtensionToString() {
    var extension = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    return extension.length === 0 ? '' : ".".concat(extension);
  }

  /**
   * Get the depth (how many directories deep it is) of the provided filename with path.
   * @param name The full file name to get the depth of
   */
  static getDepth(name) {
    var _name$match;
    if (!FileUtils.hasPath(name)) {
      throw new Error("Invalid path provided: ".concat(name));
    }
    var matches = (_name$match = name.match(/\//g)) !== null && _name$match !== void 0 ? _name$match : [];
    return matches.length - 1;
  }

  /**
   * Get just the extension of file name.
   * Note that it just returns the last extension, so 'example.tar.gz' would just return 'gz'.
   * @param name The file name with or without path to get the extension of
   * @returns The file extension
   */
  static getExtension(name) {
    var components = this.getBaseName(name).split('.');
    if (components.length > 1) {
      var _components$pop;
      return (_components$pop = components.pop()) !== null && _components$pop !== void 0 ? _components$pop : '';
    }
    return '';
  }

  /**
   * Get the base name portion of the file, eg '/foo/bar.txt' => 'bar.txt'
   * @param name The full name including path of the file
   * @returns Just the file name part of the file
   */
  static getBaseName(name) {
    var _name$split$pop;
    return (_name$split$pop = name.split('/').pop()) !== null && _name$split$pop !== void 0 ? _name$split$pop : '';
  }

  /**
   * Create copy file name, used for copying unsaved files so doesn't have to be unique
   * @param originalName File name
   * @returns The file name of the copy
   */
  static getCopyFileName(originalName) {
    var extensionPosition = originalName.lastIndexOf('.');
    var extension = null;
    var name = null;
    if (extensionPosition < 0) {
      // No extension
      name = originalName;
    } else {
      name = originalName.substring(0, extensionPosition);
      extension = originalName.substring(extensionPosition + 1);
    }
    var copyName = name ? "".concat(name, "-copy") : 'Copy';
    return extension !== null ? "".concat(copyName, ".").concat(extension) : copyName;
  }
  static getUniqueCopyFileName(fileStorage, originalName) {
    return _asyncToGenerator(function* () {
      var copyName = FileUtils.getCopyFileName(originalName);
      // await in loop is fine here, this isn't a parallel task
      // eslint-disable-next-line no-await-in-loop
      while (yield FileUtils.fileExists(fileStorage, copyName)) {
        copyName = FileUtils.getCopyFileName(copyName);
      }
      return copyName;
    })();
  }

  /**
   * Return a MIME type for the provided file
   * @param name The file name to get the type for
   * @returns A known MIME type if recognized
   */
  static getMimeType(name) {
    var extension = this.getExtension(name).toLowerCase();
    switch (extension) {
      case 'groovy':
        return MIME_TYPE.GROOVY;
      case 'py':
        return MIME_TYPE.PYTHON;
      case 'pyc':
        return MIME_TYPE.PYTHON_COMPILED;
      case 'scala':
        return MIME_TYPE.SCALA;
      case 'txt':
        return MIME_TYPE.PLAIN_TEXT;
      default:
        return MIME_TYPE.UNKNOWN;
    }
  }

  /**
   * Pop the last part of the filename component to return the parent path
   * @param name The file name to get the parent path of
   */
  static getParent(name) {
    if (!FileUtils.hasPath(name)) {
      throw new Error("Invalid name provided: ".concat(name));
    }
    var parts = name.split('/');
    while (parts.pop() === '');
    if (parts.length === 0) {
      throw new Error("No parent for path provided: ".concat(name));
    }
    return "".concat(parts.join('/'), "/");
  }

  /**
   * Get the path name portion of the file
   * @param name The full path with or without filename to get the path of
   * @returns Just the path with out the file name part, including trailing slash
   */
  static getPath(name) {
    if (!FileUtils.hasPath(name)) {
      throw new Error("Invalid filename provided: ".concat(name));
    }
    var parts = name.split('/');
    parts.pop();
    return "".concat(parts.join('/'), "/");
  }

  /**
   * Check if a given file name includes the full path
   * @param name The file name to check
   * @returns True if it's a full path, false otherwise
   */
  static hasPath(name) {
    return name.startsWith('/');
  }

  /**
   * Check a given file name is a path
   * @param name The file name to check
   * @returns True if it's a full path, false otherwise
   */
  static isPath(name) {
    return name.startsWith('/') && name.endsWith('/');
  }

  /**
   * Turns a directory file name into a path. Basically ensures there's a trailing slash
   * @param name The directory name to make a path
   */
  static makePath(name) {
    if (!name.endsWith('/')) {
      return "".concat(name, "/");
    }
    return name;
  }

  /**
   * Replace extension in the item name
   * @param name Name to replace the extension in
   * @param newExtension New extension, defaults to no extension
   */
  static replaceExtension(name) {
    var newExtension = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    var index = name.lastIndexOf('.');
    var nameWithoutExtension = index > -1 ? name.substring(0, index) : name;
    var extensionString = FileUtils.fileExtensionToString(newExtension);
    return "".concat(nameWithoutExtension).concat(extensionString);
  }

  /**
   * Validate the provided name. Throws an error if validation fails
   * @param name The item name to validate
   */
  static validateName(name) {
    // Static checks
    var reservedNames = ['.', '..'];
    // Global flag to show all invalid chars, not just the first match
    var invalidCharsRegex = /[\\/\0]/g;
    var invalidCharLabels = new Map([['\0', 'null']]);
    if (!name) {
      throw new ValidationError("Name cannot be empty");
    }
    if (reservedNames.includes(name)) {
      throw new ValidationError("\"".concat(name, "\" is a reserved name"));
    }
    if (invalidCharsRegex.test(name)) {
      var _name$match$reduce$ma, _name$match2;
      throw new ValidationError("Invalid characters in name: \"".concat((_name$match$reduce$ma = (_name$match2 = name.match(invalidCharsRegex)
      // Filter out duplicates
      ) === null || _name$match2 === void 0 ? void 0 : _name$match2.reduce((acc, next) => acc.includes(next) ? acc : [...acc, next], []).map(char => invalidCharLabels.has(char) ? invalidCharLabels.get(char) : char).join('", "')) !== null && _name$match$reduce$ma !== void 0 ? _name$match$reduce$ma : '', "\""));
    }
  }

  /**
   * Reduce the provided paths to the minimum selection.
   * Removes any nested files or directories if a parent is already selected.
   * @param paths The paths to reduce
   */
  static reducePaths(paths) {
    var folders = paths.filter(path => FileUtils.isPath(path));
    return paths.filter(path => !folders.some(folder => path !== folder && path.startsWith(folder)));
  }

  /**
   * Removes the root path from the provided file name. Throws if the filename does not start with root.
   * @param root The root to remove
   * @param filename Filename to remove the root path from
   * @returns Filename without the root
   */
  static removeRoot(root, filename) {
    if (root.length === 0) {
      return filename;
    }
    if (!filename.startsWith(root)) {
      throw new Error("Filename ".concat(filename, " does not start with expected root ").concat(root));
    }
    return filename.substring(root.length);
  }

  /**
   * Add root to the filename as prefix
   * @param path Filename to prefix root to
   * @returns Filename with root at the prefix
   */
  static addRoot(root, path) {
    return "".concat(root).concat(path);
  }
}
export default FileUtils;
//# sourceMappingURL=FileUtils.js.map